import { FC, useEffect, useState } from 'react';
import { Box, TextField, Typography, Link, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useLocation } from 'react-router-dom';

import { routes, DEFAULT_USER } from '@carcareers/utils';
import { useAuth } from '@carcareers/hooks';
import { useSnackbar } from '@carcareers/contexts';
import { Talroo, TalrooPixel } from '@carcareers/components';

export const Login: FC = () => {
  const { state } = useLocation();
  const [formData, setFormData] = useState(DEFAULT_USER);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn } = useAuth();
  const { toggleSnackbar } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'email') {
      setFormData((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          email: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const error = await signIn(formData.user.email.toLowerCase(), formData.password);

      if (error) {
        setError(error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (state?.isJobseeker && state?.isSignup) {
      toggleSnackbar(
        'success',
        <>
          <Typography>Account created successfully. Please login to continue.</Typography>
          <Talroo pixel={TalrooPixel.COMPLETE_PROFILE} />
        </>,
      );
    } else if (state?.isSignup) {
      toggleSnackbar('success', 'Account created successfully. Please login to continue.');
    }

    if (state?.isAccountDeactivated) {
      toggleSnackbar('warning', 'Account deactivated.');
    }
  }, [state]);

  return (
    <Box>
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: '500px' },
          mx: 'auto',
          pt: 12,
          px: { xs: 2, sm: 4 },
        }}
      >
        <Typography component="h1" variant="h3" align="center" my={3}>
          Login
        </Typography>
        {/* {tab === 0 ? (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                toggleSnackbar('warning', 'Login with Google not implemented');
              }}
              sx={{
                mt: 1,
                mb: 2,
                textTransform: 'none',
                borderColor: (theme) => (theme.palette.mode === 'dark' ? '#ffffff1f' : '#dadce0'),
                color: (theme) => (theme.palette.mode === 'dark' ? '#e8eaed' : '#3c4043'),
                fontFamily: 'Roboto, sans-serif',
                '&:hover': {
                  borderColor: (theme) => (theme.palette.mode === 'dark' ? '#ffffff1f' : '#dadce0'),
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(232, 234, 237, 0.04)' : '#f8f9fa'),
                },
                height: '40px',
                display: 'flex',
                gap: 1,
              }}
            >
              <Box sx={{ width: 18, height: 18 }}>
                <GoogleSvg />
              </Box>
              Sign in with Google
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Box>
          </>
        ) : null} */}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={formData.user.email}
            onChange={handleChange}
            error={!!error}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            error={!!error}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      sx={{ mr: 0 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'right',
            }}
          >
            <Link href={routes.FORGOT_PASSWORD} variant="body2">
              Forgot password?
            </Link>
          </Box>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            loading={loading}
            disabled={loading}
          >
            Log In
          </LoadingButton>
          <Box sx={{ textAlign: 'center' }}>
            <Link href={routes.JOBSEEKER_SIGNUP} variant="body2">
              {"Don't have an account? Sign Up"}
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
